import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { FormDescription, FormLabel, FormMessage } from '@ui/form.tsx';
import { StepTable } from '@admin/components/logic/step/step-table.tsx';
import { LogicTypes, type LogicType } from '@admin/types/workspace/logic.ts';
import type { LogicTypeStepWorkspaceWriteJsonMergePatchModel } from '@r/api/generated/client.schemas.ts';
import type { RenewalProgramFormSchema } from '@admin/components/renew-program/renewal-program-form-schema.ts';

interface RenewalProgramStepsStepContentProps {
  form: UseFormReturn<RenewalProgramFormSchema>;
}

/** Étape de renouvellement : définition des paliers de la prochaine logique (comme à la création workspace). */
export function RenewalProgramStepsStepContent({ form }: RenewalProgramStepsStepContentProps) {
  const { t } = useTranslation();
  const steps = form.watch('steps') ?? [];

  const handleStepsChange = useCallback(
    async (type: LogicType, values: LogicTypeStepWorkspaceWriteJsonMergePatchModel[]) => {
      if (type !== LogicTypes.STEPS) return;

      form.setValue(
        'steps',
        values.map((step) => ({
          name: step.name ?? '',
          stepPoint: step.stepPoint ?? 0,
          description: step.description ?? null
        })),
        { shouldDirty: true, shouldTouch: true, shouldValidate: true }
      );
    },
    [form]
  );

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold">{t('form.workspace.renewal.steps.title')}</h3>
        <FormDescription className="mt-1">
          {t('form.workspace.renewal.steps.description')}
        </FormDescription>
      </div>

      <div className="space-y-2">
        <FormLabel className="text-base">{t('form.workspace.logic.step')}</FormLabel>
        <StepTable data={steps} setData={handleStepsChange} />
        {form.formState.errors.steps?.message ? (
          <FormMessage>{String(form.formState.errors.steps.message)}</FormMessage>
        ) : null}
      </div>
    </div>
  );
}
